import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  List,
  ListItem,
  Paper,
  Fade,
  Grid,
  Chip,
  Tooltip,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import MedicationIcon from '@mui/icons-material/Medication';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import { medicationsAnalyzerService, RecognizeMedicationResponse } from '../../services/medications-analyzer.service';
import { CalendarEventDto } from '@/types/calendar';
import { toastService } from '../../services/toast.service';
import { localNowIso, toLocalIso } from '@/utils/datetime';
import { ModalIndices } from '../../common/i-doc/i-doc.registry.ts';
import { SchedulingBlock } from '../calendar/add-event-modals/SchedulingBlock';
import { eventColors } from '@/styles/tokens';

interface QuickMedicationAddModalV2Props {
  open: boolean;
  onClose: () => void;
  selectedDate: string; // YYYY-MM-DD
  initialScheduledAt?: Date;
  onRefreshDashboard?: () => void;
  clientId?: string;
  eventToEdit?: CalendarEventDto | null;
}

const ACCENT = eventColors.medication.main;
const ACCENT_HOVER = eventColors.medication.dark;

export const QuickMedicationAddModalV2: React.FC<QuickMedicationAddModalV2Props> = ({
  open,
  onClose,
  selectedDate,
  initialScheduledAt,
  onRefreshDashboard,
  clientId,
  eventToEdit,
}) => {
  const isEditMode = !!eventToEdit;
  const [expertMode, setExpertMode] = useState(() => isEditMode || localStorage.getItem('medication-modal-expert-mode') === 'true');
  const [inputText, setInputText] = useState('');
  const [isRecognizing, setIsRecognizing] = useState(false);
  const [recognizedEvents, setRecognizedEvents] = useState<RecognizeMedicationResponse[]>([]);
  const [suggestedTitle, setSuggestedTitle] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [showDateTimePicker, setShowDateTimePicker] = useState(!!initialScheduledAt);
  const [scheduledAt, setScheduledAt] = useState<Date | null>(initialScheduledAt || null);
  const [isCompleted, setIsCompleted] = useState(false);

  // FIX (2026-07-29, workspace/current-work/medication-add-flow-review.md):
  // раніше тут (у handleRecognize нижче) стояло `contextDate.toISOString()` —
  // СПРАВЖНІЙ UTC-рядок (з `Z`). За конвенцією (workspace/conventions/datetime.md)
  // contextDate — тип T2 (`*_ldt`), має йти БЕЗ `Z`, бо бекенд
  // (DateUtils.getEventTitleByTime) визначає "ранок/день/вечір" голим
  // `new Date(contextDate).getHours()` — без конвертації часового поясу. UTC-рядок
  // із `Z` зсуває годину на офсет користувача (для Києва влітку — на 3 години),
  // із наслідком "додав зранку — розпізналось як вечірній прийом" (і навпаки —
  // денний прийом підписувався як ранковий). Той самий патерн, що вже коректно
  // працює в QuickFoodAddModalV2/QuickWorkoutAddModalV2 (buildContextDate()) —
  // приведено до єдиного стандарту.
  const buildContextDate = () => `${selectedDate}T${localNowIso().slice(11)}`;

  React.useEffect(() => {
    if (!open) return;
    if (eventToEdit) {
      const details = eventToEdit.details as any;
      setInputText(details?.rawInput ?? '');
      const components: RecognizeMedicationResponse[] = (details?.components ?? []).map((c: any) => ({
        name: c.name ?? '',
        quantity: c.quantity ?? undefined,
        unit: c.unit ?? undefined,
        scheduledLdt: eventToEdit.scheduledLdt ?? new Date().toISOString(),
      }));
      setRecognizedEvents(components);
      setSuggestedTitle(eventToEdit.title ?? '');
      if (eventToEdit.scheduledLdt) {
        setScheduledAt(new Date(eventToEdit.scheduledLdt));
        setShowDateTimePicker(true);
      }
      setIsCompleted(eventToEdit.isCompleted ?? false);
      setExpertMode(true);
    } else {
      setInputText('');
      setRecognizedEvents([]);
      setSuggestedTitle('');
      setEditingIndex(null);
      setShowDateTimePicker(!!initialScheduledAt);
      setScheduledAt(initialScheduledAt || null);
      setIsCompleted(false);
      setExpertMode(localStorage.getItem('medication-modal-expert-mode') === 'true');
    }
  }, [open, eventToEdit]);

  const handleClose = () => {
    setInputText('');
    setRecognizedEvents([]);
    setSuggestedTitle('');
    setIsRecognizing(false);
    setEditingIndex(null);
    setShowDateTimePicker(false);
    setScheduledAt(null);
    setIsCompleted(false);
    onClose();
  };

  const handleRecognize = async () => {
    if (!inputText.trim()) return;
    setIsRecognizing(true);
    setEditingIndex(null);
    const contextDate = buildContextDate();
    // Інфо-лог (не error) — навмисно, щоб бачити в BetterStack реальний contextDate,
    // який пішов на бекенд (console.* тут глобально проксується в Logtail, див.
    // services/logger.ts). Насамперед для того, щоб швидко перевірити регресію
    // формату (без `Z`), якщо цей клас бага колись повториться.
    console.info('[QuickMedicationAddModalV2] recognize request', { contextDate, textLength: inputText.trim().length });
    try {
      const data = await medicationsAnalyzerService.recognize({
        text: inputText,
        contextDate,
      }, clientId);
      console.info('[QuickMedicationAddModalV2] recognize success', { itemsCount: data.items?.length ?? 0 });
      setRecognizedEvents(data.items);
      setSuggestedTitle(data.suggestedTitle || '');
    } catch (error: any) {
      console.error('Medication recognition error:', error);
      toastService.error(error?.message || 'Не вдалося розпізнати препарати. Спробуйте ще раз.');
    } finally {
      setIsRecognizing(false);
    }
  };

  const handleUpdateEventField = (index: number, field: keyof RecognizeMedicationResponse, value: any) => {
    const updatedEvents = [...recognizedEvents];
    const numericFields: (keyof RecognizeMedicationResponse)[] = ['quantity'];
    const parsed = numericFields.includes(field) ? (value === '' ? 0 : Number(value)) : value;
    updatedEvents[index] = { ...updatedEvents[index], [field]: parsed };
    setRecognizedEvents(updatedEvents);
  };

  const handleConfirmAll = async () => {
    try {
      const scheduledLdt = showDateTimePicker && scheduledAt ? toLocalIso(scheduledAt) : undefined;
      const finalIsCompleted = showDateTimePicker ? isCompleted : true;
      const payload = {
        items: recognizedEvents,
        rawInput: inputText || undefined,
        scheduledLdt,
        title: suggestedTitle || undefined,
        isCompleted: finalIsCompleted,
      };

      if (isEditMode && eventToEdit) {
        await medicationsAnalyzerService.updateEventsComplex(eventToEdit.id, payload);
        console.info('[QuickMedicationAddModalV2] update success', { eventId: eventToEdit.id, itemsCount: recognizedEvents.length });
        toastService.success('Препарат успішно оновлено');
      } else {
        await medicationsAnalyzerService.createEventsComplex(payload, clientId);
        console.info('[QuickMedicationAddModalV2] create success', { itemsCount: recognizedEvents.length });
        toastService.success('Препарати успішно додано');
      }
      if (onRefreshDashboard) onRefreshDashboard();
      handleClose();
    } catch (error: any) {
      console.error('Medication bulk save error:', error);
      const validationErrors = error?.response?.data?.errors;
      if (validationErrors?.length) {
        toastService.error(validationErrors.map((e: any) => e.message).join('\n'));
      } else {
        toastService.error('Не вдалося зберегти. Спробуйте ще раз.');
      }
    }
  };

  const formatTime = (isoString: string) => {
    try {
      return new Date(isoString).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  // ─── DateTime picker block (shared) ──────────────────────────────────────
  const dateTimeBlock = (
    <SchedulingBlock
      showDateTimePicker={showDateTimePicker}
      onShowDateTimePickerChange={setShowDateTimePicker}
      scheduledAt={scheduledAt}
      onScheduledAtChange={setScheduledAt}
      isCompleted={isCompleted}
      onIsCompletedChange={setIsCompleted}
      switchLabel="Встановити дату та час"
      switchSize="small"
      gap={1}
    />
  );

  // ─── Header (shared) ──────────────────────────────────────────────────────
  const header = (
    <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <MedicalServicesIcon sx={{ color: ACCENT }} />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>{isEditMode ? 'Редагувати препарат' : 'Додати препарат'}</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <FormControlLabel
          control={
            <Switch
              checked={expertMode}
              onChange={(e) => {
                const val = e.target.checked;
                setExpertMode(val);
                localStorage.setItem('medication-modal-expert-mode', String(val));
              }}
              size="small"
              sx={{
                '& .MuiSwitch-thumb': { bgcolor: expertMode ? ACCENT : undefined },
                '& .MuiSwitch-track': { bgcolor: expertMode ? `${ACCENT} !important` : undefined },
              }}
            />
          }
          label={
            <Typography variant="caption" sx={{ fontWeight: 600, color: expertMode ? ACCENT : 'text.secondary' }}>
              Режим експерта
            </Typography>
          }
          labelPlacement="start"
          sx={{ mr: 0, ml: 0 }}
        />
        <IconButton aria-label="close" onClick={handleClose} sx={{ color: (theme) => theme.palette.grey[500] }}>
          <CloseIcon />
        </IconButton>
      </Box>
    </DialogTitle>
  );

  // ─── Standard mode ────────────────────────────────────────────────────────
  if (!expertMode) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }} data-i-doc={ModalIndices.QUICK_MEDICATION_ADD}>
        {header}
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, bgcolor: 'action.hover' }}>
          <TextField
            inputRef={inputRef}
            fullWidth
            label="Які препарати ви прийняли?"
            placeholder="Наприклад: 2 таблетки аспірину, вітамін С 500мг о 10:00"
            multiline
            rows={recognizedEvents.length > 0 ? 2 : 4}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            disabled={isRecognizing}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && inputText.trim()) {
                e.preventDefault();
                handleRecognize();
              }
            }}
            sx={{ bgcolor: 'background.paper', '& .MuiInputBase-root': { borderRadius: '12px' } }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              onClick={handleRecognize}
              variant="contained"
              disabled={isRecognizing || !inputText.trim()}
              startIcon={isRecognizing ? <CircularProgress size={18} color="inherit" /> : <AutoFixHighIcon />}
              sx={{ bgcolor: ACCENT, borderRadius: '10px', px: 3, '&:hover': { bgcolor: ACCENT_HOVER } }}
            >
              {isRecognizing ? 'Аналізуємо...' : (recognizedEvents.length > 0 ? 'Аналізувати заново' : 'Аналізувати')}
            </Button>
          </Box>

          {recognizedEvents.length > 0 && (
            <Fade in>
              <Box>
                <Divider sx={{ mb: 1.5 }} />
                {suggestedTitle && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Назва події:</Typography>
                    <Chip label={suggestedTitle} size="small" sx={{ fontWeight: 600, bgcolor: '#E8F5E9', color: ACCENT }} />
                  </Box>
                )}
                <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {recognizedEvents.map((event, i) => (
                    <Paper key={i} variant="outlined" sx={{ borderRadius: '10px', px: 2, py: 1.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <MedicationIcon sx={{ color: ACCENT, flexShrink: 0 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                            {event.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {event.quantity ? `${event.quantity} ${event.unit || 'табл.'}` : ''}
                            {event.scheduledLdt ? ` · ${formatTime(event.scheduledLdt)}` : ''}
                          </Typography>
                        </Box>
                        {event.unit && (
                          <Chip
                            label={event.unit}
                            size="small"
                            sx={{ bgcolor: '#E8F5E9', color: ACCENT, fontWeight: 700, flexShrink: 0 }}
                          />
                        )}
                      </Box>
                    </Paper>
                  ))}
                </List>

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => {
                      setRecognizedEvents([]);
                      setTimeout(() => inputRef.current?.focus(), 50);
                    }}
                    sx={{ color: 'text.secondary', textDecoration: 'underline', fontSize: '0.75rem' }}
                  >
                    Не те? Уточніть запит
                  </Button>
                </Box>
              </Box>
            </Fade>
          )}
          {dateTimeBlock}
        </DialogContent>

        <DialogActions sx={{ p: 2, bgcolor: 'action.hover' }}>
          <Button onClick={handleClose} color="inherit">Скасувати</Button>
          <Button
            onClick={handleConfirmAll}
            variant="contained"
            disabled={recognizedEvents.length === 0 || isRecognizing}
            sx={{ bgcolor: ACCENT, borderRadius: '10px', px: 4, '&:hover': { bgcolor: ACCENT_HOVER } }}
          >
            {isEditMode ? 'Підтвердити та зберегти' : 'Додати'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ─── Expert mode ──────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '500px', borderRadius: '16px' } }}
      data-i-doc={ModalIndices.QUICK_MEDICATION_ADD}
    >
      {header}
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', bgcolor: 'action.hover', gap: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <TextField
            fullWidth
            label="Які препарати ви прийняли?"
            placeholder="Наприклад: 2 таблетки аспірину, вітамін С 500мг о 10:00"
            variant="outlined"
            multiline
            rows={recognizedEvents.length > 0 ? 3 : 6}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            disabled={isRecognizing}
            sx={{
              bgcolor: 'background.paper',
              transition: 'all 0.3s ease',
              '& .MuiInputBase-root': { borderRadius: '12px' },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              onClick={handleRecognize}
              variant="contained"
              disabled={isRecognizing || !inputText.trim()}
              startIcon={isRecognizing ? <CircularProgress size={20} color="inherit" /> : <AutoFixHighIcon />}
              sx={{ backgroundColor: ACCENT, borderRadius: '10px', px: 3, '&:hover': { backgroundColor: ACCENT_HOVER } }}
            >
              {isRecognizing ? 'Аналізуємо...' : (recognizedEvents.length > 0 ? 'Аналізувати заново' : 'Аналізувати')}
            </Button>
          </Box>
        </Box>

        {suggestedTitle !== '' && (
          <TextField
            fullWidth
            label="Назва події"
            value={suggestedTitle}
            onChange={(e) => setSuggestedTitle(e.target.value)}
            sx={{ bgcolor: 'background.paper', '& .MuiInputBase-root': { borderRadius: '12px' } }}
          />
        )}

        {recognizedEvents.length > 0 && (
          <Fade in={recognizedEvents.length > 0}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#282828' }}>
                Розпізнані записи:
              </Typography>
              <List disablePadding>
                {recognizedEvents.map((event, index) => (
                  <Paper
                    key={index}
                    variant="outlined"
                    sx={{
                      mb: 1.5,
                      borderRadius: '12px',
                      overflow: 'hidden',
                      borderColor: editingIndex === index ? ACCENT : '#e0e0e0',
                      borderWidth: editingIndex === index ? 2 : 1,
                    }}
                  >
                    <ListItem sx={{ py: 1.5, flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
                          <MedicationIcon sx={{ color: ACCENT }} />
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          {editingIndex === index ? (
                            <TextField
                              fullWidth
                              size="small"
                              label="Назва препарату"
                              value={event.name}
                              onChange={(e) => handleUpdateEventField(index, 'name', e.target.value)}
                            />
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                {event.name}
                              </Typography>
                              {event.originalText && (
                                <Tooltip title={`Оригінальний текст: "${event.originalText}"`}>
                                  <InfoIcon sx={{ fontSize: '1rem', color: '#A8A8A8', cursor: 'help' }} />
                                </Tooltip>
                              )}
                              <Tooltip title={`Час прийому: ${formatTime(event.scheduledLdt)}`}>
                                <AccessTimeIcon sx={{ fontSize: '1rem', color: '#A8A8A8', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          )}
                        </Box>
                        <Button
                          size="small"
                          variant={editingIndex === index ? 'contained' : 'text'}
                          color={editingIndex === index ? 'secondary' : 'inherit'}
                          onClick={() => setEditingIndex(editingIndex === index ? null : index)}
                          startIcon={editingIndex === index ? <SaveIcon /> : <EditIcon />}
                          sx={{ ml: 1 }}
                        >
                          {editingIndex === index ? 'Зберегти' : 'Змінити'}
                        </Button>
                      </Box>

                      <Grid container spacing={2} sx={{ width: '100%', ml: 4 }}>
                        <Grid item xs={6} sm={4}>
                          {editingIndex === index ? (
                            <TextField
                              fullWidth
                              size="small"
                              label="Кількість"
                              type="number"
                              value={event.quantity || ''}
                              onChange={(e) => handleUpdateEventField(index, 'quantity', e.target.value)}
                            />
                          ) : (
                            <Typography variant="body2"><b>Кількість:</b> {event.quantity || 0}</Typography>
                          )}
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          {editingIndex === index ? (
                            <TextField
                              fullWidth
                              size="small"
                              label="Од. виміру"
                              value={event.unit || ''}
                              placeholder="табл, капс, мг..."
                              onChange={(e) => handleUpdateEventField(index, 'unit', e.target.value)}
                            />
                          ) : (
                            <Typography variant="body2"><b>Од. виміру:</b> {event.unit || '-'}</Typography>
                          )}
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2" color="text.secondary">
                            <b>Час:</b> {formatTime(event.scheduledLdt)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </ListItem>
                  </Paper>
                ))}
              </List>
            </Box>
          </Fade>
        )}
        {dateTimeBlock}
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: 'action.hover' }}>
        <Button onClick={handleClose} color="inherit">Скасувати</Button>
        <Button
          onClick={handleConfirmAll}
          variant="contained"
          disabled={recognizedEvents.length === 0 || isRecognizing || editingIndex !== null}
          sx={{ backgroundColor: ACCENT, borderRadius: '10px', px: 4, '&:hover': { backgroundColor: ACCENT_HOVER } }}
        >
          Підтвердити та зберегти
        </Button>
      </DialogActions>
    </Dialog>
  );
};
